import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { UniqueIdentifier, DragEndEvent } from "@dnd-kit/core";
import {
  DndContext,
  closestCorners
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove
} from "@dnd-kit/sortable";

import {
  EllipsisVertical
} from "@unified-checkout/design-system/icons/index";
import type { IButton } from "libs/design-system/src/types/Button/button";
import { getPaymentMethod, Modal } from "@unified-checkout/design-system";

import { useCheckoutCustomizationStore } from "@app/dashboard/store/checkout-customization";
import { useDndKitSensors } from "@app/dashboard/hooks/dnd_kit/useDndKitSensors";
import type {
  IMethodDraggableCard,
  TMethodDraggableCardBase,
  TModalProps,
  TConfirmationModal,
  TXPayMethodDraggableCardBase
} from "@app/dashboard/types/innerLayout/checkout_customization/payment-methods";
import { DraggableCard } from "@app/dashboard/components/inner_layout/DraggableCard";
import { Title } from "../../../../components/Title";
import {
  MODAL_TYPES,
  PAY_LATER,
  sortByRank
} from "./utils";
import { useConfirm } from "./useConfirm";
import styles from "./payment-methods.module.scss";

const { ENABLE, DISABLE } = MODAL_TYPES;

const { RANK, GROUP_RANK } = {
  RANK: 'rank',
  GROUP_RANK: 'group_rank'
} as const;

export const PaymentMethods = ({
  groupPayLater,
  listingType,
}: {
  groupPayLater: boolean,
  listingType: 'tab' | 'list' | undefined,
}) => {
  const { t } = useTranslation();

  const {
    isFetched,
    isDataReset,
    setCustomizationData
  } = useCheckoutCustomizationStore();
  const { payments } = useCheckoutCustomizationStore.getState().customizationData;
  const { x_pay, bnpl, methods: apiMethods } = payments;

  const orderBy = (!groupPayLater && (listingType === 'list')) ?
    RANK : GROUP_RANK;

  const [xPayMethods, setXPayMethods] = useState<TXPayMethodDraggableCardBase[]>([]);
  const [methods, setMethods] = useState<IMethodDraggableCard[]>([]);

  const [isInitialized, setIsInitialized] = useState(false);
  const [modalProps, setModalProps] = useState<TModalProps>({
    open: false,
    text: '',
    hint: '',
  });

  const {
    confirmationInquiry,
    handleCancel,
    handleConfirm
  } = useConfirm(setModalProps);


  useEffect(() => {
    if (isFetched) {

      setXPayMethods(x_pay.map(method => ({
        id: method.gateway,
        label: getPaymentMethod(method.gateway).name,
        value: method.name,
        icon: getPaymentMethod(method.gateway).icon,
        active: method.active,
        childable: false,
        draggable: false,
        collapsible: false,
        apiData: method
      })) as TXPayMethodDraggableCardBase[]);

      const bnpl_methods = sortByRank(bnpl, orderBy).map(method => ({
        id: method.gateway,
        label: getPaymentMethod(method.gateway).name,
        value: method.name,
        icon: getPaymentMethod(method.gateway).icon,
        active: method.active,
        childable: true,
        draggable: true,
        collapsible: false,
        apiData: method
      }));

      setMethods(
        sortByRank(apiMethods, orderBy).map(method => ({
          id: method.gateway,
          label: getPaymentMethod(method.gateway).name,
          value: method.name,
          icon: getPaymentMethod(method.gateway).icon,
          active: method.active,
          draggable: true,
          childable: false,
          collapsible: false,
          ...(
            method.gateway === PAY_LATER ?
              {
                childrenProps: bnpl_methods,
                collapsible: true,
                collapsed: true,
              } : []
          ),
          apiData: method
        })));


      setIsInitialized(true);
    }
  }, [isFetched, isDataReset, orderBy]);

  useEffect(() => {
    if (isInitialized) {
      const x_pay_methods = xPayMethods.map(m => m.apiData);

      const payLaterParent = methods.find(m => m.id === PAY_LATER);
      const bnpl_methods = payLaterParent?.childrenProps?.map(ch => ch.apiData) || [];

      const filteredOrdinaryMethods = methods.filter(m => !m.childable);
      const ordinary_methods = filteredOrdinaryMethods.map(m => m.apiData);

      setCustomizationData(prevData => ({
        ...prevData,
        payments: {
          ...prevData.payments,
          x_pay: x_pay_methods,
          bnpl: bnpl_methods,
          methods: ordinary_methods
        }
      }));
    }
  }, [methods]);

  useEffect(() => {

    isInitialized && setMethods(prev => {
      const ordinaryMethods = prev.filter(card => !card.childable);
      const payLaterParent = prev.find(card => card.id === PAY_LATER);
      const children = payLaterParent?.childrenProps || [];

      if (!groupPayLater && (listingType === 'list')) {
        return [
          ...ordinaryMethods,
          ...children
        ];
      } else {
        return ordinaryMethods;
      }
    })
  }, [groupPayLater, listingType, t, isInitialized]);


  const getCardPosition = (id: string | UniqueIdentifier) => {
    return methods?.findIndex(card => card.id === id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id === over?.id) return;

    setMethods((cards) => {
      const originalPosition = getCardPosition(active.id);
      const newPosition = getCardPosition(over?.id || '');

      const newSorting =
        arrayMove(cards, originalPosition, newPosition)
          .map((m, index) => ({ ...m, apiData: { ...m.apiData, [orderBy]: index } }));

      if (!groupPayLater) {
        const payLaterMethods = newSorting.filter(card => card.childable);
        const updatedSorting = newSorting.map(card =>
          card.id === PAY_LATER ? { ...card, childrenProps: payLaterMethods } : card
        );
        return updatedSorting;
      };

      return newSorting;
    });
  };

  const modalDetails = useCallback((type: TConfirmationModal, paymentMethod: string) => {

    const cancelBtnProps = {
      text: t('form.cancel'),
      color: 'secondary',
      variant: 'outlined',
      onClickHandler: () => handleCancel()
    };

    const details = {
      [ENABLE]: {
        text: t('innerLayout.checkoutCustomizationPage.tabs.payment.modals.enablePaymentMethod.title', { payment_method: paymentMethod }),
        hint: t('innerLayout.checkoutCustomizationPage.tabs.payment.modals.enablePaymentMethod.hint'),
        btnsProps: [
          {
            text: t('innerLayout.checkoutCustomizationPage.tabs.payment.modals.enablePaymentMethod.ctas.primary', { payment_method: paymentMethod }),
            onClickHandler: () => handleConfirm(),
          },
          cancelBtnProps
        ] as IButton[]
      },
      [DISABLE]: {
        text: t('innerLayout.checkoutCustomizationPage.tabs.payment.modals.disablePaymentMethod.title', { payment_method: paymentMethod }),
        hint: t('innerLayout.checkoutCustomizationPage.tabs.payment.modals.disablePaymentMethod.hint'),
        btnsProps: [
          {
            text: t('innerLayout.checkoutCustomizationPage.tabs.payment.modals.disablePaymentMethod.ctas.primary', { payment_method: paymentMethod }),
            onClickHandler: () => handleConfirm(),
            color: 'danger'
          },
          cancelBtnProps
        ] as IButton[]
      },
    };

    return details[type];
  }, [t, handleConfirm, handleCancel]);

  const updateToggleState = (currentState: boolean, id: string) => {

    setMethods(prev =>
      prev.map(method => {
        if (method.id === id) {
          return { ...method, active: !currentState, apiData: { ...method.apiData, active: !currentState } };
        }
        if (method.id === PAY_LATER && method.childrenProps) {
          return {
            ...method,
            childrenProps: method.childrenProps.map(child =>
              child.id === id
                ? { ...child, active: !currentState, apiData: { ...child.apiData, active: !currentState } }
                : child
            )
          };
        }
        return method;
      })
    );

    setXPayMethods(prev =>
      prev.map(method =>
        method.id === id
          ? { ...method, active: !currentState, apiData: { ...method.apiData, active: !currentState } }
          : method
      )
    );
  };

  const handleConfirmationBeforeToggle = async (
    currentState: boolean, id: string
  ): Promise<boolean> => {

    const modalData = modalDetails(currentState ? DISABLE : ENABLE, id);

    setModalProps(prev => ({
      ...prev,
      ...modalData
    }));

    const confirmed = await confirmationInquiry();

    confirmed && updateToggleState(currentState, id);

    return confirmed;
  };

  const sensors = useDndKitSensors();

  const descriptionParts = t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.paymentMethods.desc').split('⋮');

  return (
    <>
      <div className={styles["payment-methods"]}>
        <Title
          label={t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.paymentMethods.label')}
          hint={t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.paymentMethods.hint')}
        />

        <div className={styles["payment-methods__desc"]}>
          {descriptionParts[0]}
          <span className={styles["payment-methods__desc__drag-icon"]}>{EllipsisVertical}</span>
          {descriptionParts[1]}
        </div>

        <div className={styles["payment-methods__sortable-items"]}>
          {
            xPayMethods.map((card) => {
              return (
                <div
                  key={`x-pay-${card.id}`}
                >
                  <DraggableCard
                    {...card}
                    orderBy={orderBy}
                    onBeforeToggleConfirmation={handleConfirmationBeforeToggle}
                  />
                </div>
              )
            }
            )
          }
          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            collisionDetection={closestCorners}
          >
            <SortableContext
              items={methods as ({ id: UniqueIdentifier })[]}
              strategy={verticalListSortingStrategy}
            >
              {
                methods.sort((a, b) => (a.apiData[orderBy] || 0) - (b.apiData[orderBy] || 0))
                  .filter(card => (groupPayLater || listingType === 'tab') ? card : card.id !== PAY_LATER)
                  .map((card) => {
                    return (
                      <div
                        key={card.id}
                      >
                        <DraggableCard
                          {...card}
                          orderBy={orderBy}
                          onBeforeToggleConfirmation={handleConfirmationBeforeToggle}
                          onSwap={(parentId, childCards) => setMethods((cards) =>
                            cards.map(c => c.id === parentId ? { ...c, childrenProps: childCards as TMethodDraggableCardBase[] } : c)
                          )}
                          onCollapse={(id, collapsed) => setMethods((cards) => {
                            return cards.map(c => c.id === id ? { ...c, collapsed: collapsed } : c)
                          })}
                        />
                      </div>
                    )
                  }
                  )
              }
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <Modal
        open={modalProps.open}
        title={modalProps.text}
        supportingText={modalProps.hint}
        hasCloseIcon
        onCloseHandler={handleCancel}
        btnsProps={modalProps.btnsProps}
      />
    </>

  )
}
